import { MouseEvent, ReactNode } from 'react';
import { sprintf } from 'sprintf-js';

import { ApiUserEnvironmentOrgMini } from 'api/userEnvironment/types/ApiUserEnvironmentOrgMini';
import { Box } from 'components/Box/Box';
import { AnchorButton } from 'components/Button/AnchorButton';
import { Button } from 'components/Button/Button';
import { Dropdown } from 'components/Dropdown/Dropdown';
import { closeDropdownFromElementWithin } from 'components/Dropdown/closeDropdownFromElementWithin';
import { DropdownListMenu } from 'components/Dropdown/menus/ListMenu/DropdownListMenu';
import {
  DropdownListMenuItem,
  DropdownListMenuItemRouter,
} from 'components/Dropdown/menus/ListMenu/DropdownListMenuItem';
import {
  DropdownListMenuSection,
  DropdownListMenuSectionTitle,
} from 'components/Dropdown/menus/ListMenu/DropdownListMenuSection';
import { Icon } from 'components/Icon';
import { userDashboardSavedItemsRoute } from 'modules/userDashboard/userDashboard.routes';
import { trackClicked } from 'utils/analytics/track/trackClicked';

import {
  MyAccountMobileDropdownBody,
  MyAccountMobileDropdownFooter,
  MyAccountMobileDropdownHeader,
  MyAccountMobileDropdownHolder,
  MyAccountMobileDropdownLink,
  MyAccountMobileDropdownSection,
  MyAccountMobileDropdownSectionTitle,
  MyDropdownMobileHeaderTitle,
} from './MyAccountDropdown.styled';
import { MyAccountDropdownProfileSection } from './MyAccountDropdownProfileSection';

type Props = {
  trigger: ReactNode;
  displayName: string;
  orgs: ApiUserEnvironmentOrgMini[];
  promoteResumeDb: boolean;
  showEmploymentProfileLink: boolean;
  hasResume: boolean;
  isLoggedIn: boolean;
  loginLink: string;
  onClickLogin: (event: MouseEvent<HTMLAnchorElement>) => void;
};

export function MyAccountDropdown({
  trigger,
  displayName,
  orgs,
  promoteResumeDb,
  showEmploymentProfileLink,
  hasResume,
  isLoggedIn,
  loginLink,
  onClickLogin,
}: Props) {
  return (
    <Dropdown trigger={trigger}>
      <DropdownListMenu height="calc(100vh - 80px)" qaId="nav-dropdown">
        {promoteResumeDb && (
          <MyAccountDropdownProfileSection hasResume={hasResume} />
        )}

        {orgs.length > 0 && (
          <DropdownListMenuSection>
            <DropdownListMenuSectionTitle>
              {getText('Manage')}
            </DropdownListMenuSectionTitle>

            {orgs.map((org) => (
              <DropdownListMenuItemRouter
                key={org.id}
                to={
                  org.hasGradProgram && !org.overrideToMainDashboard
                    ? `/admin/${org.id}/grad-program`
                    : `/admin/${org.id}`
                }
                onClick={(event) => {
                  trackClicked('Header Dropdown Link', {
                    link_type: 'Organization',
                    url:
                      org.hasGradProgram && !org.overrideToMainDashboard
                        ? `/admin/${org.id}/grad-program`
                        : `/admin/${org.id}`,
                  });
                  closeDropdownFromElementWithin(event.target as HTMLElement);
                }}
              >
                <span className="truncate">{org.name}</span>
              </DropdownListMenuItemRouter>
            ))}
          </DropdownListMenuSection>
        )}

        <DropdownListMenuSection>
          <DropdownListMenuSectionTitle>
            {getText('My Account')}
          </DropdownListMenuSectionTitle>

          <DropdownListMenuItemRouter
            data-qa-id="page-header-account-dropdown-saved-items"
            to="/dashboard/saved-items"
            onClick={(event) => {
              trackClicked('Header Dropdown Link', {
                link_type: 'My Saved Items',
                url: '/dashboard/saved-items',
              });
              closeDropdownFromElementWithin(event.target as HTMLElement);
            }}
          >
            <Icon name="bookmark" size={16} />
            <Box display="inline-block" ml={2}>
              {getText('My Saved Items')}
            </Box>
          </DropdownListMenuItemRouter>

          {showEmploymentProfileLink && (
            <DropdownListMenuItemRouter
              data-qa-id="page-header-account-dropdown-employment-profile"
              to="/dashboard/employment-profile"
              onClick={(event) => {
                trackClicked('Header Dropdown Link', {
                  link_type: 'Employment profile',
                  url: '/dashboard/employment-profile',
                });
                closeDropdownFromElementWithin(event.target as HTMLElement);
              }}
            >
              <Icon name="person" size={16} />
              <Box display="inline-block" ml={2}>
                {getText('Profile')}
              </Box>
            </DropdownListMenuItemRouter>
          )}

          <DropdownListMenuItemRouter
            data-qa-id="page-header-account-dropdown-account-settings"
            to="/dashboard/account-settings"
            onClick={() => {
              trackClicked('Header Dropdown Link', {
                link_type: 'Settings',
                url: '/dashboard/account-settings',
              });
            }}
          >
            <Icon name="gear" size={16} />
            <Box display="inline-block" ml={2}>
              {getText('Settings')}
            </Box>
          </DropdownListMenuItemRouter>
        </DropdownListMenuSection>

        <DropdownListMenuSection>
          <DropdownListMenuItem
            href="/logout"
            data-qa-id="logout-link"
            $logoutLink
          >
            {getText('Log Out')}
          </DropdownListMenuItem>
        </DropdownListMenuSection>
      </DropdownListMenu>

      <MyAccountMobileDropdownHolder>
        <MyAccountMobileDropdownHeader>
          <MyDropdownMobileHeaderTitle>
            {displayName
              ? /* TRANSLATORS: user greeting at top of page */
                sprintf(getText('Hi, %(name)s!'), {
                  name: displayName,
                })
              : getText('Menu')}
          </MyDropdownMobileHeaderTitle>

          <Button
            onClick={(event) =>
              closeDropdownFromElementWithin(event.target as HTMLElement)
            }
            aria-label={getText('Close search')}
            variant="tertiary-grey"
            icon={{ type: 'normalized', name: 'close' }}
          />
        </MyAccountMobileDropdownHeader>

        <MyAccountMobileDropdownBody data-qa-id="nav-dropdown-mobile">
          <MyAccountMobileDropdownSection>
            <MyAccountMobileDropdownLink
              to={userDashboardSavedItemsRoute.with({})}
            >
              {getText('My Saved Items')}
            </MyAccountMobileDropdownLink>

            {showEmploymentProfileLink && (
              <MyAccountMobileDropdownLink to="/dashboard/employment-profile">
                {getText('Profile')}
              </MyAccountMobileDropdownLink>
            )}

            <MyAccountMobileDropdownLink
              data-qa-id="page-header-account-dropdown-account-settings"
              to="/dashboard/account-settings"
            >
              {getText('Settings')}
            </MyAccountMobileDropdownLink>
          </MyAccountMobileDropdownSection>

          {orgs.length > 0 && (
            <MyAccountMobileDropdownSection>
              <MyAccountMobileDropdownSectionTitle>
                {getText('Manage')}
              </MyAccountMobileDropdownSectionTitle>

              {orgs.map((org) => (
                <div key={org.id}>
                  <MyAccountMobileDropdownLink
                    to={
                      org.hasGradProgram
                        ? `/admin/${org.id}/grad-program`
                        : `/admin/${org.id}`
                    }
                  >
                    {org.name}
                  </MyAccountMobileDropdownLink>
                </div>
              ))}
            </MyAccountMobileDropdownSection>
          )}

          <MyAccountMobileDropdownFooter>
            {isLoggedIn ? (
              <AnchorButton href="/logout" size="large" variant="danger">
                {getText('Log Out')}
              </AnchorButton>
            ) : (
              <AnchorButton
                size="large"
                href={loginLink}
                onClick={onClickLogin}
                data-qa-id="login-button-link"
                aria-label={getText('Log In')}
                variant="secondary"
              >
                {getText('Log In')}
              </AnchorButton>
            )}
          </MyAccountMobileDropdownFooter>
        </MyAccountMobileDropdownBody>
      </MyAccountMobileDropdownHolder>
    </Dropdown>
  );
}
